import { APIContext } from "@certn/api";
import type { components } from "@certn/api/generated-schemas/applicant-portal";
import { useContext, useEffect, useMemo, useState } from "react";
import { matchPath, useLocation } from "react-router";

import {
  INVITE_OPENED_CHANNELS,
  recordInviteOpened,
  type InviteOpenedChannel,
} from "../api/recordInviteOpened";

/** How long to hold AuthOutlet before rendering it anyway. */
export const INVITE_OPENED_GATE_MS = 1500;

const SESSION_KEY_PREFIX = "applicant-portal:invite-opened";

const inviteOpenedInflight = new Map<string, Promise<void>>();

export function inviteOpenedSessionKey(caseId: string): string {
  return `${SESSION_KEY_PREFIX}:${caseId}`;
}

/** Drops in-flight entries so hook tests do not leak across cases. */
export function clearInviteOpenedInflightForTests(): void {
  inviteOpenedInflight.clear();
}

type ApplyInvite = {
  caseId: string;
  subjectId: string;
  inviteChannel?: InviteOpenedChannel;
};

function parseInviteChannel(
  value: string | null
): InviteOpenedChannel | undefined {
  if (!value) return undefined;
  const normalized = value.trim().toUpperCase();
  return INVITE_OPENED_CHANNELS.find((channel) => channel === normalized);
}

function readSession(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeSession(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // Privacy mode can throw. Still attempt the post; the in-flight map
    // prevents a second request for the rest of this page load.
  }
}

function readApplyInvite(pathname: string, search: string): ApplyInvite | null {
  const match = matchPath({ path: "/apply/:caseId", end: false }, pathname);
  const caseId = match?.params.caseId;
  if (!caseId) return null;

  const params = new URLSearchParams(search);
  const subjectId = params.get("subject_id");
  if (!subjectId) return null;

  return {
    caseId,
    subjectId,
    inviteChannel: parseInviteChannel(params.get("invite_channel")),
  };
}

function postInviteOpenedOnce(
  domain: string,
  invite: ApplyInvite
): Promise<void> {
  const storageKey = inviteOpenedSessionKey(invite.caseId);
  const existing = inviteOpenedInflight.get(storageKey);
  if (existing) return existing;

  if (readSession(storageKey)) return Promise.resolve();

  const body: components["schemas"]["InviteOpened"] = {
    subject_id: invite.subjectId,
  };
  if (invite.inviteChannel) {
    body.invite_channel = invite.inviteChannel;
  }

  // The in-flight map dedupes remounts in this document, including React
  // strict mode. The session marker is written only after a 2xx so an Auth0
  // redirect that cancels the POST — including during the CORS preflight,
  // which keepalive does not cover — is retried when the applicant returns.
  // A second POST is safe: the API keeps the first channel and timestamp.
  const pending = recordInviteOpened(domain, invite.caseId, body)
    .then(() => {
      writeSession(storageKey, "1");
    })
    .finally(() => {
      if (inviteOpenedInflight.get(storageKey) === pending) {
        inviteOpenedInflight.delete(storageKey);
      }
    });
  inviteOpenedInflight.set(storageKey, pending);
  return pending;
}

/**
 * Records first arrival on `/apply/:caseId` links that carry `subject_id`.
 *
 * Returns false until the POST settles or {@link INVITE_OPENED_GATE_MS}
 * elapses, whichever comes first. Timeouts, network errors, and non-2xx
 * responses still return true so AuthOutlet can render. The timeout does
 * not abort the fetch. `keepalive` covers a POST that is already on the
 * wire; if the document unloads before a 2xx, the session marker stays
 * unset and the next load retries.
 */
export function useRecordInviteOpened(): boolean {
  const { pathname, search } = useLocation();
  const { domain } = useContext(APIContext);
  const invite = useMemo(
    () => readApplyInvite(pathname, search),
    [pathname, search]
  );
  const inviteKey = invite
    ? `${invite.caseId}:${invite.subjectId}:${invite.inviteChannel ?? ""}`
    : null;
  const [releasedKey, setReleasedKey] = useState<string | null>(null);

  const storageKey = invite ? inviteOpenedSessionKey(invite.caseId) : null;
  const alreadyRecorded =
    storageKey != null &&
    readSession(storageKey) != null &&
    !inviteOpenedInflight.has(storageKey);

  useEffect(() => {
    if (!invite || !inviteKey || !storageKey) return;

    let active = true;
    const release = () => {
      if (active) setReleasedKey(inviteKey);
    };

    if (readSession(storageKey) && !inviteOpenedInflight.has(storageKey)) {
      release();
      return () => {
        active = false;
      };
    }

    if (!domain) {
      release();
      return () => {
        active = false;
      };
    }

    // Let the applicant through even if the POST is still pending. Clearing
    // this timer must not abort the fetch.
    const timeoutId = window.setTimeout(release, INVITE_OPENED_GATE_MS);

    void postInviteOpenedOnce(domain, invite)
      .catch(() => {
        // Fail open: a network error or non-2xx must not block AuthOutlet.
      })
      .finally(() => {
        window.clearTimeout(timeoutId);
        release();
      });

    return () => {
      active = false;
      window.clearTimeout(timeoutId);
    };
  }, [domain, invite, inviteKey, storageKey]);

  return invite == null || alreadyRecorded || releasedKey === inviteKey;
}
